/**
 * YoolPayMes — el mes como calendario. Una celda por día, y desde ahí se entra al día.
 *
 * QUÉ REEMPLAZA
 * -------------
 * `YoolPayCierre` tenía 3.164 líneas y cinco pasos MENSUALES sobre un ciclo que es diario:
 * mezclaba la caja del mes, la revisión por compañía, la facturación, el calendario de
 * asientos y la aprobación en una sola pantalla. Al lado convivían otras ocho entradas de
 * menú —tres mirando lo mismo con nombres distintos, una matriz de pruebas de QA y una de
 * configuración de tarifas— sin nada que dijera cuál era el recorrido.
 *
 * El recorrido es: MES → DÍA. Nada más.
 *
 * ESTA PANTALLA NO EJECUTA NADA, Y ÉSA ES LA DECISIÓN DE DISEÑO
 * ------------------------------------------------------------
 * No hay un solo botón que mueva plata. Todo lo que se ejecuta vive en el día, porque es ahí
 * donde ocurre: el PSP deposita el lote de CADA día, y se gira por día y por compañía. Una
 * acción a nivel mes tendría que decidir sobre treinta días a la vez — que es exactamente el
 * modelo viejo, donde la plata del día 1 se transfería junto con la del 30.
 *
 * El mes responde UNA pregunta: **¿qué días me faltan y por dónde sigo?**
 *
 * TRES ESTADOS Y NO DOS
 * ---------------------
 * Un día sin cobros NO es un día pendiente. Pintarlos igual llenaría el calendario de celdas
 * que parecen exigir algo donde no hay nada que hacer — el mismo defecto que la jornada tenía
 * con ocho bloques en cero, multiplicado por treinta.
 *
 * DOS COLUMNAS: EL MES Y LA COLA
 * ------------------------------
 * El calendario responde «¿cómo viene el mes?» de un vistazo; la columna de al lado responde
 * «¿qué hago ahora?» sin obligar a recorrer 31 celdas comparando colores. Antes esa segunda
 * pregunta la contestaban dos KPIs que contaban DÍAS —«días por cerrar», «días liquidados»—
 * al lado de dos que contaban PLATA: cuatro tarjetas que hacían cambiar de unidad en cada una
 * y que igual no decían CUÁLES días faltaban ni por qué.
 *
 * @see src/services/yoolpay/mesApi.js — de dónde vienen los datos
 * @see src/pages/payments/yoolpay/YoolPayDia.jsx — el día, donde se ejecuta
 * @see src/components/payments/yoolpay/KpisDelMes.jsx — la plata del mes
 * @see src/components/payments/yoolpay/ColaDelMes.jsx — los días que piden algo
 * @see src/components/payments/yoolpay/cierre/ds.js — por qué los componentes salen del DS
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

import { getMes } from '../../../services/yoolpay/mesApi';
import { Aviso, Rotulo, Seccion } from '../../../components/payments/yoolpay/cierre/piezas';
import { Badge, Boton, StatusPill } from '../../../components/payments/yoolpay/cierre/ds';
import KpisDelMes, { pspsDelMes } from '../../../components/payments/yoolpay/KpisDelMes';
import ColaDelMes from '../../../components/payments/yoolpay/ColaDelMes';
import {
  fmtClp,
  fmtPeriodo,
  hoyUtc,
  periodoActual,
  primerDiaSemana,
  sumarMeses,
} from '../../../lib/yoolpay/formato';

const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

/**
 * Estado del día → clase del calendario del prototipo (`.cw-day`).
 *
 * EL COLOR VA EN UNA BARRA, NO EN TODA LA CELDA. Con 31 celdas, teñir el fondo entero
 * convierte el mes en un semáforo donde el ojo no distingue «hay que mirarlo» de «no
 * cuadra»: la celda queda blanca y el estado va en 3px abajo.
 *
 * Y LOS COLORES SON LOS DEL DS. El ámbar del pendiente era `#D35400`, un naranja crudo que
 * no es token ni es el ámbar del diseño (`#F59E0B`, `--yool-warn`) — y el naranja está
 * prohibido por la guía. Ahora sale de la hoja de estilos, que usa el token.
 *
 *   liquidado       ok    verde   — cerrado, no hay nada que hacer
 *   pendiente       warn  ámbar   — espera algo, y la celda dice qué
 *   sin_movimiento  none  gris    — no hubo cobros. NO es una tarea.
 */
const CLASE = {
  liquidado: 'ok',
  pendiente: 'warn',
  sin_movimiento: 'none',
};

/**
 * El día que además está SIN RESOLVER: la plata del PSP ya está y la jornada sigue abierta,
 * o Odoo rechazó un asiento.
 *
 * ES UN CUARTO ASPECTO Y NO UN CUARTO ESTADO. `estado` sigue siendo el del cierre —cerró o no
 * cerró— y esto sólo lo tiñe. Existe porque la cola de al lado marca esos días en rojo, y una
 * fila roja apuntando a una celda ámbar deja al operador preguntándose cuál de las dos vistas
 * le está mintiendo. La que decide es `severidad`, en `payment`; acá sólo se pinta.
 */
const CLASE_SIN_RESOLVER = 'err';

/**
 * En qué está el período, para el pill que va junto al título.
 *
 * CUATRO ESTADOS, y el que importa es el segundo: «con diferencias» no es lo mismo que «en
 * curso». Un mes en curso va bien y le quedan días por vivir; uno con diferencias tiene plata
 * del PSP ya depositada que no salió, o un asiento que Odoo rechazó — cosas que no se
 * resuelven esperando y que un «en curso» amable tapa hasta el cierre.
 *
 * LA DISTINCIÓN SALE DE `severidad`, no de un umbral propio. Es el mismo criterio con que la
 * cola pinta sus filas en rojo, así que el pill del título y la lista no pueden discrepar.
 *
 * `null` sin datos: un pill sobre una llamada fallida afirma algo del mes que no se sabe.
 */
function estadoDelPeriodo(resumen, dias) {
  if (!resumen) return null;
  if (resumen.dias_con_movimiento === 0) {
    return { pill: 'inactivo', label: 'Sin movimiento' };
  }
  if ((dias ?? []).some((d) => d.severidad === 'sin_resolver')) {
    return { pill: 'pausado', label: 'Con diferencias' };
  }
  if (resumen.dias_pendientes > 0) return { pill: 'procesando', label: 'En curso' };
  return { pill: 'activo', label: 'Cerrado' };
}

/** Los seis pasos, con el mismo nombre que usa la pantalla del día. */
const PASO_LABEL = {
  cobros: 'Cobros',
  deposito_esperado: 'Depósito esperado',
  aceptar_deposito: 'Aceptar depósito',
  contabilidad: 'Contabilidad',
  pedidos: 'Pedidos',
  giro: 'Giro',
};

/**
 * Una celda del calendario.
 *
 * Un día sin movimiento NO es clickeable: entrar a él muestra seis pasos «sin materia», que
 * es un viaje para no encontrar nada.
 */
function Celda({ dia, onIr, hoy }) {
  const clase = dia.severidad === 'sin_resolver'
    ? CLASE_SIN_RESOLVER
    : CLASE[dia.estado] ?? CLASE.sin_movimiento;
  const clickeable = dia.estado !== 'sin_movimiento';
  const numero = Number(dia.fecha.slice(8));
  // Un día que todavía no llega no pide nada, y atenuarlo lo dice sin sacarlo del mes.
  const futuro = hoy != null && dia.fecha > hoy;

  return (
    <button
      type="button"
      className={`cw-day ${clase}${futuro ? ' fut' : ''}`}
      onClick={clickeable ? () => onIr(dia.fecha) : undefined}
      disabled={!clickeable}
      // EL MOTIVO VA EN EL TOOLTIP y no en la celda: en 76px de alto no cabe una frase, y
      // recortarla dejaría medio diagnóstico. La cola de al lado lo muestra completo.
      title={clickeable
        ? `${dia.fecha} · ${dia.cobros} cobro${dia.cobros === 1 ? '' : 's'}`
          + `${dia.motivo ? ` — ${dia.motivo}` : ''}`
        : `${dia.fecha} · sin cobros`}
    >
      <span className="cw-dn">{numero}</span>

      {dia.estado === 'sin_movimiento' ? null : (
        <>
          <span
            className="cw-mono"
            style={{ fontSize: 13, fontWeight: 600, color: 'var(--yool-black)' }}
          >
            {fmtClp(dia.cobrado_clp)}
          </span>
          {/* QUÉ FALTA, no cuánto falta: «Giro» dice qué hacer, «2 pasos» obliga a entrar
              para averiguarlo. El conteo va al lado y en chico, para distinguir un día recién
              empezado de uno al que sólo le falta el último paso. */}
          <span className="cw-dv">
            {dia.siguiente
              ? `${PASO_LABEL[dia.siguiente] ?? dia.siguiente}`
                + `${dia.pasos_pendientes > 1 ? ` +${dia.pasos_pendientes - 1}` : ''}`
              : 'Cerrado'}
          </span>
        </>
      )}
      {/* La barra del estado. Va al final y `margin-top:auto` la pega abajo, así todas
          las celdas la muestran a la misma altura aunque tengan distinto contenido. */}
      <span className="cw-dbar" />
    </button>
  );
}

export default function YoolPayMes() {
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [period, setPeriod] = useState(periodoActual);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  // `data === null` con `error` es «la llamada falló», distinto de un mes sin movimiento.
  // Los ceros de una respuesta fallida se leen como «mes tranquilo», que es lo peor que
  // puede decir una pantalla de plata.
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const token = await getToken();
      setData(await getMes(token, { period }));
    } catch (e) {
      setData(null);
      setError(e?.message ?? 'No se pudo cargar el mes');
    } finally {
      setCargando(false);
    }
  }, [getToken, period]);

  useEffect(() => { load(); }, [load]);

  const irAlDia = useCallback(
    (fecha) => navigate(`/yoolpay/dia?fecha=${fecha}`),
    [navigate],
  );

  /**
   * Las celdas, con el relleno de la primera semana.
   *
   * Sin el relleno, el 1 caería siempre en la columna del domingo y las columnas dejarían de
   * ser días de la semana. En una pantalla de operación eso importa: el PSP deposita en días
   * hábiles.
   */
  const celdas = useMemo(() => {
    const dias = data?.dias ?? [];
    if (dias.length === 0) return [];
    return [...Array(primerDiaSemana(period)).fill(null), ...dias];
  }, [data, period]);

  const resumen = data?.resumen ?? null;
  const estado = estadoDelPeriodo(resumen, data?.dias);
  // Para atenuar los días que todavía no llegan. UTC, igual que el ciclo de cobro:
  // el corte del día lo decide `charge_date`, no la hora local de quien mira.
  const hoy = hoyUtc();

  return (
    // LAS CUATRO PROPIEDADES DEL VIEWPORT, las mismas que `YoolPayDia`. La clave es
    // `minWidth: 0`: sin él un contenedor flex se ensancha hasta el contenido más ancho que
    // tenga adentro —una tabla, el calendario— y arrastra scroll horizontal a TODA la
    // página, en vez de dejar que scrollee sólo el bloque que lo necesita.
    //
    // El día lo tiene desde tres commits (edd563f0 → 098758bc → d352c08b, que fue el que
    // dio con `minWidth`). Al mes nunca se le aplicó.
    <div
      style={{
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {/* ── Encabezado del período ──────────────────────────────────────────────────
          SIN TARJETA, a diferencia del resto de la pantalla. El título del período no es
          una sección más entre las otras: es lo que dice QUÉ se está mirando, y meterlo en
          un recuadro lo pone al mismo nivel visual que el calendario que describe. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 24,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '.16em',
              textTransform: 'uppercase',
              // El teal está reservado al CTA y a este rótulo, igual que el `eyebrow` de
              // `Modal`: es el único lugar donde marca de qué producto es la pantalla.
              color: 'var(--yool-cta)',
            }}
          >
            {/* EL PSP SE NOMBRA SI HAY UNO. Un mes sin cobros no tiene proveedor, y dejar
                «· VIRTUALPOS» fijo diría que ese proveedor cobró cuando no lo hizo. */}
            {['Cobro y liquidación diarios', 'Reporte mensual', pspsDelMes(resumen)]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginTop: 9,
              flexWrap: 'wrap',
            }}
          >
            <h1
              style={{
                margin: 0,
                fontSize: 30,
                fontWeight: 700,
                letterSpacing: '-.035em',
                textTransform: 'capitalize',
              }}
            >
              {fmtPeriodo(period)}
            </h1>
            {estado ? <StatusPill estado={estado.pill}>{estado.label}</StatusPill> : null}
          </div>
          <p
            style={{
              margin: '9px 0 0',
              fontSize: 14,
              color: 'var(--yool-gray1)',
              lineHeight: 1.5,
              maxWidth: 700,
              textWrap: 'pretty',
            }}
          >
            El sistema cobra a diario y <strong>liquida a diario</strong>: cada jornada tiene
            que cuadrar sola —lo recaudado ese día es exactamente lo que se gira ese día,
            compañía por compañía—. El mes no es donde se liquida: es donde se{' '}
            <strong>reporta</strong>, se concilia lo que quedó abierto y se cierra
            contablemente.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <Boton
            variant="outline"
            onClick={() => setPeriod((p) => sumarMeses(p, -1))}
            aria-label="Mes anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </Boton>
          <Boton
            variant="outline"
            onClick={() => setPeriod((p) => sumarMeses(p, 1))}
            aria-label="Mes siguiente"
          >
            <ChevronRight className="h-4 w-4" />
          </Boton>
          <Boton variant="ghost" onClick={load} disabled={cargando}>
            <RefreshCw className={`h-4 w-4 ${cargando ? 'animate-spin' : ''}`} />
            {' '}Actualizar
          </Boton>
          {/* El documento que se le manda a la compañía. Sólo lectura: el ciclo se sigue
              ejecutando en el día, y esta pantalla nunca movió plata. */}
          <Boton
            variant="outline"
            onClick={() => navigate(`/yoolpay/informe/mes?period=${period}`)}
            title="El documento de liquidación que se le manda a la compañía"
          >
            Informe
          </Boton>
        </div>
      </div>

      {/* EL ERROR ANTES DE LAS CIFRAS. Unos KPIs en cero sobre una llamada fallida se leen
          como un mes tranquilo, que es lo peor que puede decir una pantalla de plata. */}
      {error ? (
        <Aviso tono="alta" titulo="No se pudo cargar el mes">
          {error} — la pantalla está vacía porque la llamada falló, no porque no hubiera
          movimiento.
        </Aviso>
      ) : null}

      {resumen ? <KpisDelMes resumen={resumen} /> : null}

      <section
        style={{
          display: 'grid',
          // 372px FIJOS para la cola: es una lista de texto, y una columna elástica la
          // dejaría o demasiado angosta para el motivo o robándole ancho al calendario, que
          // tiene siete columnas que no se pueden comprimir.
          gridTemplateColumns: resumen ? 'minmax(0, 1fr) 372px' : 'minmax(0, 1fr)',
          gap: 20,
          alignItems: 'start',
        }}
      >
        <Seccion
          titulo="Calendario"
          bajada={
            'Un día por celda. Los días sin cobros no se pueden abrir: no hay nada que hacer '
            + 'en ellos.'
          }
          derecha={
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Badge variant="exito" soft>Liquidado</Badge>
              <Badge variant="advertencia" soft>Pendiente</Badge>
              <Badge variant="neutro" soft>Sin cobros</Badge>
            </div>
          }
        >
          <div style={{ padding: '18px 24px 24px' }}>
            {cargando && !data ? (
              <p style={{ fontSize: 13, color: 'var(--yool-muted)', margin: 0 }}>
                Cargando el mes…
              </p>
            ) : celdas.length === 0 ? (
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  fontSize: 13, color: 'var(--yool-muted)',
                }}
              >
                <CalendarDays className="h-5 w-5" />
                {error ? 'Sin datos: la llamada falló.' : 'Este mes no tiene días que mostrar.'}
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  {DIAS_SEMANA.map((d) => (
                    <Rotulo key={d} align="center">{d}</Rotulo>
                  ))}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                    gap: 8,
                  }}
                >
                  {celdas.map((dia, i) => (
                    dia == null
                      // El relleno de la primera semana. Un div vacío y no una celda gris: un
                      // recuadro ahí se leería como un día sin movimiento.
                      // eslint-disable-next-line react/no-array-index-key
                      ? <div key={`pad-${i}`} aria-hidden />
                      : <Celda key={dia.fecha} dia={dia} onIr={irAlDia} hoy={hoy} />
                  ))}
                </div>
              </>
            )}
          </div>
        </Seccion>

        {/* LA COLA SÓLO CON DATOS. Sin `resumen` no se puede distinguir «ningún día quedó
            con pendientes» de «la llamada falló», y la primera frase sobre la segunda es
            una felicitación falsa. */}
        {resumen ? <ColaDelMes dias={data.dias} resumen={resumen} onIr={irAlDia} /> : null}
      </section>
    </div>
  );
}
